import React, { useEffect, useState } from "react";
import { AppShell } from "@/components/AppShell";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { fmtDate, fmtDateTime } from "@/lib/utils-app";
import { ShieldCheck, ShieldX, Check, X, MapPin } from "lucide-react";
import { toast } from "sonner";

function StatusBadge({ status }) {
  if (status === "approved") return <Badge className="bg-emerald-600 text-white hover:bg-emerald-600"><ShieldCheck className="mr-1 h-3 w-3" />Approved</Badge>;
  if (status === "rejected") return <Badge className="bg-rose-600 text-white hover:bg-rose-600"><ShieldX className="mr-1 h-3 w-3" />Rejected</Badge>;
  return <Badge variant="outline">Pending</Badge>;
}

export default function CleaningLocationRequestsPage() {
  const [filter, setFilter] = useState("pending");
  const [list, setList] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const params = filter === "all" ? {} : { status: filter };
    const { data } = await api.get("/admin/cleaning-location-requests", { params });
    setList(data);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [filter]);

  const decide = async (id, approve) => {
    setBusy(true);
    try {
      await api.post(`/admin/cleaning-location-requests/${id}/decide`, { approve, note: null });
      toast.success(approve ? "Approved" : "Rejected");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <AppShell
      title="Cleaning location requests"
      subtitle="Approve a supervisor starting Before-cleaning at a new park while an earlier park's After-cleaning is still pending"
    >
      <div className="flex flex-col gap-4">
        <Card>
          <CardContent className="flex items-center gap-3 p-3">
            <MapPin className="h-4 w-4 text-primary" />
            <span className="text-sm">Showing</span>
            <Select value={filter} onValueChange={setFilter}>
              <SelectTrigger className="w-44" data-testid="cleaning-location-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
                <SelectItem value="rejected">Rejected</SelectItem>
                <SelectItem value="all">All</SelectItem>
              </SelectContent>
            </Select>
          </CardContent>
        </Card>

        {list.length === 0 ? (
          <Card><CardContent className="p-8 text-center text-sm text-muted-foreground">Nothing here.</CardContent></Card>
        ) : (
          <div className="grid gap-2" data-testid="cleaning-location-list">
            {list.map((r) => (
              <Card key={r.id} data-testid={`cleaning-location-${r.id}`}>
                <CardContent className="flex flex-wrap items-start gap-3 p-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <div className="font-medium">{r.supervisor_name}</div>
                      <StatusBadge status={r.status} />
                    </div>
                    <div className="mt-1 text-xs text-muted-foreground">
                      {fmtDate(r.date)}
                      {r.created_at ? <> · requested {fmtDateTime(r.created_at)}</> : null}
                    </div>
                    <div className="mt-1.5 text-sm">
                      {r.request_type === "add_work_location" || r.asset_name ? (
                        <>
                          Requested new work location: <span className="font-semibold text-emerald-800">{r.asset_name}</span>{" "}
                          ({r.asset_type || "work location"}) at <span className="font-medium">{r.park_name}</span>.
                        </>
                      ) : (
                        <>
                          Wants to start <span className="font-medium">{r.new_park_name}</span> while{" "}
                          <span className="font-medium">{r.pending_park_name}</span>&apos;s After-cleaning is still pending.
                        </>
                      )}
                    </div>
                    {r.reason ? (
                      <div className="mt-1.5 rounded bg-muted/60 p-2 text-xs text-foreground font-medium">
                        <span className="font-semibold text-muted-foreground uppercase text-[10px]">Business Justification / Remarks:</span>
                        <p className="mt-0.5">{r.reason}</p>
                      </div>
                    ) : null}
                    {r.decision_note && (
                      <div className="mt-1 text-xs text-muted-foreground">Note: {r.decision_note}</div>
                    )}
                  </div>
                  {r.status === "pending" && (
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => decide(r.id, false)}
                        disabled={busy}
                        data-testid={`cleaning-location-${r.id}-reject`}
                      >
                        <X className="mr-1 h-4 w-4" /> Reject
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => decide(r.id, true)}
                        disabled={busy}
                        data-testid={`cleaning-location-${r.id}-approve`}
                      >
                        <Check className="mr-1 h-4 w-4" /> Approve
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </AppShell>
  );
}
